import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { categoryLabel, asset } from "../data/portfolio";

// Card media — static still (16:9) + round lime ▶ badge; on hover the still scales
// and cover.mp4 fades in and plays muted/loop. Touch devices never hover → still only.
function CardMedia({ cover, title }) {
  const vref = useRef(null);
  const onEnter = () => { const v = vref.current; if (v) { v.muted = true; v.play?.().catch(() => {}); } };
  const onLeave = () => { const v = vref.current; if (v) { v.pause?.(); } };
  return (
    <div className="card-media" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <img src={asset(cover?.still || cover?.poster)} alt={title} loading="lazy" />
      <span className="play" aria-hidden>▶</span>
      <video ref={vref} muted loop playsInline preload="none" poster={asset(cover?.poster)}>
        {cover?.webm && <source src={asset(cover.webm)} type="video/webm" />}
        {cover?.mp4 && <source src={asset(cover.mp4)} type="video/mp4" />}
      </video>
    </div>
  );
}

// Full project card — still cover + title, client, role, chip, year.
export function ProjectCard({ project, number, eager = false, dark = false }) {
  const chipStyle = dark
    ? { height: 26, fontSize: 9, borderColor: "rgba(255,255,255,.4)", color: "#fff" }
    : { height: 26, fontSize: 9 };
  return (
    <Link to={`/work/${project.slug}`} className="card block group" data-testid={`card-${project.slug}`}>
      <CardMedia cover={project.cover} title={project.title} />
      <div className="mt-3">
        <div className="flex items-baseline gap-3">
          {number && <span className="mono" style={{ color: "var(--grey)" }}>{number}</span>}
          <h3 className="h2" style={{ fontSize: "clamp(1.1rem,1.8vw,1.6rem)", color: dark ? "var(--lime)" : "var(--blue)", width: "100%" }}>{project.title}</h3>
        </div>
        <p className="mono mt-1" style={{ color: dark ? "rgba(255,255,255,.7)" : "var(--grey)" }}>{project.client} · {project.role?.split(",")[0] || categoryLabel(project.category)}</p>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <span className="chip" style={chipStyle}>{categoryLabel(project.category)}</span>
          <span className="mono" style={{ color: dark ? "rgba(255,255,255,.7)" : "var(--grey)" }}>{project.year}</span>
          {(project.tags || []).map((t) => <span key={t} className="ftag ftag--lime">{t.toUpperCase()}</span>)}
        </div>
      </div>
    </Link>
  );
}

// Index table with a cursor-following preview image on row hover.
export function IndexTable({ items, testid = "index-table" }) {
  const [preview, setPreview] = useState(null);
  const pos = useRef({ x: 0, y: 0 });
  const imgRef = useRef(null);

  const onMove = (e) => {
    pos.current = { x: e.clientX, y: e.clientY };
    if (imgRef.current) {
      imgRef.current.style.transform = `translate(${e.clientX + 24}px, ${e.clientY - 180}px) rotate(-4deg)`;
    }
  };

  return (
    <div onMouseMove={onMove} data-testid={testid}>
      <div className="mono rule-b pb-2 grid grid-cols-[40px_1fr_60px] md:grid-cols-[48px_1fr_1fr_120px_70px]" style={{ color: "var(--grey)" }}>
        <span>No</span><span>Name</span><span className="hidden md:block">Services</span><span className="hidden md:block">Sector</span><span className="text-right">Year</span>
      </div>
      {items.map((p, i) => (
        <Link
          key={p.slug}
          to={`/work/${p.slug}`}
          className="grid items-center rule-b group grid-cols-[40px_1fr_60px] md:grid-cols-[48px_1fr_1fr_120px_70px]"
          style={{ padding: "16px 0", transition: "background .25s var(--ease), color .25s var(--ease), padding-left .25s var(--ease)" }}
          onMouseEnter={(e) => { setPreview(p); e.currentTarget.style.background = "var(--lime)"; e.currentTarget.style.paddingLeft = "14px"; }}
          onMouseLeave={(e) => { setPreview(null); e.currentTarget.style.background = "transparent"; e.currentTarget.style.paddingLeft = "0"; }}
          data-testid={`index-row-${p.slug}`}
        >
          <span className="mono" style={{ color: "var(--grey)" }}>{String(i + 1).padStart(2, "0")}</span>
          <span className="h2" style={{ fontSize: "clamp(18px,1.8vw,26px)", color: "var(--blue)" }}>{p.title}</span>
          <span className="mono hidden md:block" style={{ color: "var(--ink)" }}>{(p.deliverables || []).slice(0, 2).join(", ")}</span>
          <span className="mono hidden md:block">{p.industry}</span>
          <span className="mono text-right">{p.year}</span>
        </Link>
      ))}
      {preview && (
        <img
          ref={imgRef}
          src={asset(preview.cover?.poster)}
          alt=""
          className="hidden lg:block pointer-events-none fixed top-0 left-0 z-[90]"
          style={{ width: 300, aspectRatio: "16/9", objectFit: "cover", borderRadius: 6, boxShadow: "0 20px 50px rgba(0,0,0,.3)" }}
        />
      )}
    </div>
  );
}
